import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/Tabs';
import { Users, MapPin, Activity, Shield, UserX, Filter, BarChart2, AlertTriangle, Map, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { FilterBar, FilterField } from '../components/FilterBar';
import { SyncButton } from '../components/SyncButton';
import { PersonnelMap } from '../components/PersonnelMap';
import { useScreenInit } from '../useScreenInit.js';
import { getRoster, getProvinsiOptions, getBindaOptions, getRoleOptions, getExecutiveSummary, STATUS_OPERATIF_LIST, statusMarkerColor, isOnline, type StatusOperatif } from '../data/personnelOps';
function OperatifBadge({
  status
}: {
  status: StatusOperatif;
}) {
  const color = statusMarkerColor(status);
  return <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{
    color,
    borderColor: color,
    backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`
  }} data-id="element-2625">
      <span className="w-1.5 h-1.5 rounded-full" style={{
      backgroundColor: color
    }} data-id="element-2626" />
      {status}
    </span>;
}
export default function Personnel() {
  const navigate = useNavigate();
  const screenInit = useScreenInit();
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState(screenInit?.personnelTab || 'daftar');
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const roster = useMemo(() => getRoster(), []);
  const filteredPersonnel = useMemo(() => {
    return roster.filter(p => {
      const search = filterValues.search?.toLowerCase() || '';
      const matchesSearch = !search || p.name.toLowerCase().includes(search) || p.id.toLowerCase().includes(search);
      const matchesProvinsi = !filterValues.provinsi || filterValues.provinsi === 'all' || p.provinsi === filterValues.provinsi;
      const matchesBinda = !filterValues.binda || filterValues.binda === 'all' || p.bindaKode === filterValues.binda;
      const matchesRole = !filterValues.role || filterValues.role === 'all' || p.role === filterValues.role;
      const matchesStatus = !filterValues.status || filterValues.status === 'all' || p.status === filterValues.status;
      const matchesOnline = !filterValues.onlineStatus || filterValues.onlineStatus === 'all' || (filterValues.onlineStatus === 'Online' ? isOnline(p.status) : !isOnline(p.status));
      return matchesSearch && matchesProvinsi && matchesBinda && matchesRole && matchesStatus && matchesOnline;
    });
  }, [roster, filterValues]);
  const execSummary = useMemo(() => getExecutiveSummary(filteredPersonnel), [filteredPersonnel]);
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: 'Cari nama, ID...'
  }, {
    key: 'provinsi',
    label: 'Provinsi',
    type: 'select',
    options: getProvinsiOptions().map(p => ({
      label: p,
      value: p
    }))
  }, {
    key: 'binda',
    label: 'BINDA',
    type: 'select',
    options: getBindaOptions().map(b => ({
      label: b.binda,
      value: b.kode
    }))
  }, {
    key: 'role',
    label: 'Peran',
    type: 'select',
    options: getRoleOptions().map(r => ({
      label: r,
      value: r
    }))
  }, {
    key: 'onlineStatus',
    label: 'Status Koneksi',
    type: 'select',
    options: [{
      label: 'Online',
      value: 'Online'
    }, {
      label: 'Offline',
      value: 'Offline'
    }]
  }, {
    key: 'status',
    label: 'Status Operatif',
    type: 'statusBadge',
    options: STATUS_OPERATIF_LIST.map(s => ({
      label: s,
      value: s
    }))
  }];
  const exportColumns = [{
    key: 'id',
    label: 'ID Personel'
  }, {
    key: 'name',
    label: 'Nama Lengkap'
  }, {
    key: 'role',
    label: 'Peran'
  }, {
    key: 'provinsi',
    label: 'Provinsi'
  }, {
    key: 'status',
    label: 'Status'
  }, {
    key: 'kpiScore',
    label: 'KPI'
  }];
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-2627">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 border-b border-border/50 pb-4" data-id="element-2628">
        <div data-id="element-2629">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-2630">
            PEMANTAUAN PERSONEL
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-2631">
            Operasi Nasional & Pelacakan Pergerakan
          </p>
        </div>
        <div className="flex gap-3 items-center w-full sm:w-auto" data-id="element-2632">
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs flex-1 sm:flex-none ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-2633">
            <Filter className="w-4 h-4 mr-2" data-id="element-2634" />
            Filter
          </Button>
          <SyncButton data-id="element-2635" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2636">
        <Card className="glass-panel-heavy border-l-4 border-l-primary shadow-[0_0_15px_rgba(var(--primary),0.1)]" data-id="element-2637">
          <CardContent className="p-4 flex items-center gap-4" data-id="element-2638">
            <div className="p-3 bg-primary/10 rounded border border-primary/30" data-id="element-2639">
              <Users className="w-5 h-5 text-primary" data-id="element-2640" />
            </div>
            <div data-id="element-2641">
              <p className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest" data-id="element-2642">
                Total Personel
              </p>
              <p className="text-2xl font-mono font-bold text-foreground text-glow-primary" data-id="element-2643">
                {execSummary.total}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-panel-heavy border-l-4 border-l-[var(--status-active)] shadow-[0_0_15px_rgba(var(--status-active),0.1)]" data-id="element-2644">
          <CardContent className="p-4 flex items-center gap-4" data-id="element-2645">
            <div className="p-3 bg-[var(--status-active)]/10 rounded border border-[var(--status-active)]/30" data-id="element-2646">
              <Activity className="w-5 h-5 text-[var(--status-active)]" data-id="element-2647" />
            </div>
            <div data-id="element-2648">
              <p className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest" data-id="element-2649">
                Aktif / Online
              </p>
              <p className="text-2xl font-mono font-bold text-[var(--status-active)] text-glow-active" data-id="element-2650">
                {execSummary.aktif}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-panel-heavy border-l-4 border-l-[var(--status-info)] shadow-[0_0_15px_rgba(var(--status-info),0.1)]" data-id="element-2651">
          <CardContent className="p-4 flex items-center gap-4" data-id="element-2652">
            <div className="p-3 bg-[var(--status-info)]/10 rounded border border-[var(--status-info)]/30" data-id="element-2653">
              <MapPin className="w-5 h-5 text-[var(--status-info)]" data-id="element-2654" />
            </div>
            <div data-id="element-2655">
              <p className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest" data-id="element-2656">
                Sedang Bertugas
              </p>
              <p className="text-2xl font-mono font-bold text-[var(--status-info)] text-glow-info" data-id="element-2657">
                {execSummary.bertugas}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-panel-heavy border-l-4 border-l-destructive shadow-[0_0_15px_rgba(var(--destructive),0.1)]" data-id="element-2658">
          <CardContent className="p-4 flex items-center gap-4" data-id="element-2659">
            <div className="p-3 bg-destructive/10 rounded border border-destructive/30" data-id="element-2660">
              <AlertTriangle className="w-5 h-5 text-destructive" data-id="element-2661" />
            </div>
            <div data-id="element-2662">
              <p className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest" data-id="element-2663">
                Panic Alert
              </p>
              <p className="text-2xl font-mono font-bold text-destructive text-glow-destructive" data-id="element-2664">
                {execSummary.alert}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredPersonnel.length} exportData={filteredPersonnel} exportColumns={exportColumns} exportFilename="Data_Personel_DENS_CAKRA" data-id="element-2665" />}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="element-2666">
        <TabsList className="grid w-full max-w-md grid-cols-3 mb-6" data-id="element-2667">
          <TabsTrigger value="daftar" className="font-mono text-xs uppercase tracking-widest" data-id="element-2668">
            <List className="w-4 h-4 mr-2" data-id="element-2669" /> Daftar
          </TabsTrigger>
          <TabsTrigger value="peta" className="font-mono text-xs uppercase tracking-widest" data-id="element-2670">
            <Map className="w-4 h-4 mr-2" data-id="element-2671" /> Peta Nasional
          </TabsTrigger>
          <TabsTrigger value="eksekutif" className="font-mono text-xs uppercase tracking-widest" data-id="element-2672">
            <BarChart2 className="w-4 h-4 mr-2" data-id="element-2673" /> Eksekutif
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daftar" className="mt-0" data-id="element-2674">
          <Card className="glass-panel-heavy border-border/50 shadow-[0_0_20px_rgba(0,0,0,0.5)]" data-id="element-2675">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2676">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2677">
                <Users className="w-4 h-4 text-primary" data-id="element-2678" /> Daftar Personel
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0" data-id="element-2679">
              <Table data-id="element-2680">
                {filteredPersonnel.length === 0 ? <EmptyState icon={UserX} title="PERSONEL TIDAK DITEMUKAN" description="Tidak ada data personel yang cocok dengan filter Anda." actionLabel="Bersihkan Filter" onAction={handleResetFilters} className="border-0 rounded-none rounded-b-lg" data-id="element-2681" /> : <>
                    <TableHeader className="bg-black/20" data-id="element-2682">
                      <TableRow className="border-border/30 hover:bg-transparent" data-id="element-2683">
                        <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2684">
                          ID
                        </TableHead>
                        <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2685">
                          Personel
                        </TableHead>
                        <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2686">
                          Peran & Unit
                        </TableHead>
                        <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2687">
                          Wilayah
                        </TableHead>
                        <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2688">
                          Status
                        </TableHead>
                        <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right" data-id="element-2689">
                          KPI
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-id="element-2690">
                      {filteredPersonnel.map(p => <TableRow key={p.id} className="border-border/20 hover:bg-white/5 transition-colors cursor-pointer border-l-2 border-l-transparent" onClick={() => navigate(`/personnel/${p.id}`)} data-id="element-2691">
                          <TableCell className="font-mono text-xs text-muted-foreground" data-id="element-2692">
                            {p.id}
                          </TableCell>
                          <TableCell data-id="element-2693">
                            <div className="flex items-center gap-3" data-id="element-2694">
                              <img src={p.photo} alt={p.name} className="w-8 h-8 rounded-full border border-border/50" data-id="element-2695" />
                              <span className="font-medium text-sm" data-id="element-2696">
                                {p.name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell data-id="element-2697">
                            <div className="text-xs" data-id="element-2698">{p.role}</div>
                            <div className="text-[10px] text-muted-foreground font-mono" data-id="element-2699">
                              {p.unit}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs font-mono" data-id="element-2700">
                            {p.provinsi}
                          </TableCell>
                          <TableCell data-id="element-2701">
                            <OperatifBadge status={p.status} data-id="element-2702" />
                          </TableCell>
                          <TableCell className="text-right" data-id="element-2703">
                            <span className={`font-mono font-bold ${p.kpiScore >= 90 ? 'text-primary' : p.kpiScore >= 80 ? 'text-[var(--status-active)]' : 'text-muted-foreground'}`} data-id="element-2704">
                              {p.kpiScore}
                            </span>
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </>}
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="peta" className="mt-0" data-id="element-2705">
          <Card className="glass-panel-heavy border-border/50 shadow-[0_0_20px_rgba(0,0,0,0.5)]" data-id="element-2706">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2707">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2708">
                <Map className="w-4 h-4 text-primary" data-id="element-2709" /> Peta Sebaran Personel
                Nasional
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0" data-id="element-2710">
              {filteredPersonnel.length === 0 ? <EmptyState icon={MapPin} title="TIDAK ADA DATA LOKASI" description="Tidak ada personel yang sesuai dengan filter untuk ditampilkan di peta." data-id="element-2711" /> : <>
                  <div className="flex flex-wrap gap-4 p-4 border-b border-border/50 bg-black/20" data-id="element-2712">
                    {[{
                  c: 'var(--status-active)',
                  l: 'Aktif'
                }, {
                  c: 'var(--status-standby)',
                  l: 'Siaga'
                }, {
                  c: 'var(--status-info)',
                  l: 'Bertugas / Patroli'
                }, {
                  c: 'var(--destructive)',
                  l: 'Panic Alert'
                }, {
                  c: 'var(--muted-foreground)',
                  l: 'Offline'
                }].map(item => <div key={item.l} className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-id="element-2713">
                        <span className="w-3 h-3 rounded-full" style={{
                    backgroundColor: item.c
                  }} data-id="element-2714" />
                        {item.l}
                      </div>)}
                  </div>
                  <PersonnelMap personnel={filteredPersonnel} height={600} data-id="element-2715" />
                </>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="eksekutif" className="mt-0" data-id="element-2716">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2717">
            <Card className="glass-panel border-border/50" data-id="element-2718">
              <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2719">
                <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2720">
                  <Activity className="w-4 h-4 text-primary" data-id="element-2721" /> Ringkasan Status
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4" data-id="element-2722">
                <div className="flex justify-between items-center p-3 bg-black/20 rounded border border-border/30" data-id="element-2723">
                  <span className="text-sm font-mono text-muted-foreground" data-id="element-2724">
                    Aktif / Online
                  </span>
                  <span className="text-lg font-mono font-bold text-[var(--status-active)]" data-id="element-2725">
                    {execSummary.aktif}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-black/20 rounded border border-border/30" data-id="element-2726">
                  <span className="text-sm font-mono text-muted-foreground" data-id="element-2727">
                    Sedang Bertugas
                  </span>
                  <span className="text-lg font-mono font-bold text-[var(--status-info)]" data-id="element-2728">
                    {execSummary.bertugas}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-black/20 rounded border border-border/30" data-id="element-2729">
                  <span className="text-sm font-mono text-muted-foreground" data-id="element-2730">
                    Sedang Patroli
                  </span>
                  <span className="text-lg font-mono font-bold text-[var(--status-standby)]" data-id="element-2731">
                    {execSummary.patroli}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-black/20 rounded border border-border/30" data-id="element-2732">
                  <span className="text-sm font-mono text-muted-foreground" data-id="element-2733">
                    Offline
                  </span>
                  <span className="text-lg font-mono font-bold text-muted-foreground" data-id="element-2734">
                    {execSummary.offline}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel border-border/50" data-id="element-2735">
              <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2736">
                <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2737">
                  <MapPin className="w-4 h-4 text-primary" data-id="element-2738" /> Distribusi per
                  Wilayah
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0" data-id="element-2739">
                <Table data-id="element-2740">
                  <TableHeader className="bg-black/20" data-id="element-2741">
                    <TableRow className="border-border/30" data-id="element-2742">
                      <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2743">
                        Provinsi
                      </TableHead>
                      <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right" data-id="element-2744">
                        Personel
                      </TableHead>
                      <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right" data-id="element-2745">
                        Alerts
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-2746">
                    {execSummary.byRegion.slice(0, 8).map(r => <TableRow key={r.provinsi} className="border-border/20" data-id="element-2747">
                        <TableCell className="text-xs font-mono" data-id="element-2748">
                          {r.provinsi}
                        </TableCell>
                        <TableCell className="text-right font-mono" data-id="element-2749">
                          {r.count}
                        </TableCell>
                        <TableCell className="text-right font-mono" data-id="element-2750">
                          {r.alert > 0 ? <span className="text-destructive font-bold" data-id="element-2751">
                              {r.alert}
                            </span> : <span className="text-muted-foreground" data-id="element-2752">-</span>}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>;
}